import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormArray } from '@angular/forms';
import { ClientConfigService } from 'client-config-service-lib';
import { Namespace, SectionType, TemplateUsage } from 'enums-lib';
import { WorkflowConfigComponent } from '../workflow-config.component';

@Component({
  selector: 'app-awesomedoc-workflow-config',
  templateUrl: './awesomedoc-workflow-config.component.html',
  styleUrls: ['./awesomedoc-workflow-config.component.css', '../workflow-config.component.scss'],
  standalone: false,
})
export class AwesomedocWorkflowConfigComponent implements OnInit {
  get fileSection() {
    return this.findSection(this.parent.sectionsFormArray, SectionType.File);
  }

  get mergeSection() {
    return this.findSection(this.parent.sectionsFormArray, SectionType.Merge);
  }

  @Input() parent: WorkflowConfigComponent;
  @Output() delete = new EventEmitter<void>();
  @Output() copyDirectLink = new EventEmitter<void>();
  @Output() discardLocalChanges = new EventEmitter<any>();
  @Output() updateDocumentForDestinationType = new EventEmitter<any>();
  @Output() onDocxFileNameChange = new EventEmitter<any>();
  @Output() saveWorkflowConfig = new EventEmitter<void>();

  protected readonly SectionType = SectionType;
  protected readonly TemplateUsage = TemplateUsage;

  private readonly destroyRef = inject(DestroyRef);
  private readonly clientConfigService = inject(ClientConfigService);

  wasLoadedWithDocMerge = false;
  isDocMergeEnabled = false;

  ngOnInit(): void {
    const initialTypes: TemplateUsage[] = this.parent.usageTypesControl.value ?? [];
    this.wasLoadedWithDocMerge = initialTypes.includes(TemplateUsage.DocMerge);

    this.clientConfigService.isDocMergeEnabled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.isDocMergeEnabled = enabled;
      });

    this.parent.configuration
      .get('useFileAttachmentAsTemplate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((useAttachment: boolean) => {
        if (!useAttachment) {
          return;
        }

        const current: TemplateUsage[] = this.parent.usageTypesControl.value ?? [];
        if (!current.includes(TemplateUsage.DocMerge)) {
          return;
        }

        const remaining = current.filter((t) => t !== TemplateUsage.DocMerge);
        this.parent.usageTypesControl.setValue(remaining.length > 0 ? remaining : [TemplateUsage.AwesomeDocs]);
      });
  }

  get showActionsTab() {
    const isEntitySelected = !!this.parent.entity;
    const isBullhorn = this.parent.namespace === Namespace.bullhorn;
    const isSalesforce = this.parent.namespace === Namespace.salesforce;

    return (isBullhorn || isSalesforce) && isEntitySelected;
  }

  get showUsageTypesToggle(): boolean {
    const isPublicOrHadDocMerge = this.parent.configuration.get('isPublic')?.value || this.wasLoadedWithDocMerge;
    switch (this.parent.namespace) {
      case Namespace.invenias:
        return false;
      case Namespace.salesforce:
        return this.isDocMergeEnabled && isPublicOrHadDocMerge;
      default:
        return isPublicOrHadDocMerge;
    }
  }

  onUsageTypesChange(selected: TemplateUsage[]): void {
    if (!selected || selected.length === 0) {
      this.parent.usageTypesControl.setValue([TemplateUsage.AwesomeDocs]);
    }
  }

  private findSection(formArray: UntypedFormArray, sectionType: SectionType) {
    return formArray.controls.find((section) => section.get('type')?.value === sectionType);
  }
}
